import React, { useEffect, useState } from 'react';
import { apiClient } from '../../services/apiClient';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';

interface RevenueCharts {
  by_source: { source: string; revenue: string }[];
  by_tenant_top_10: { tenant_name: string; tenant_id: string; revenue: string }[];
}

const COLORS = Array.from({ length: 9 }, (_, i) => `var(--color-chart-palette-${i})`);

const Revenue: React.FC = () => {
  const [data, setData] = useState<RevenueCharts | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCharts = async () => {
      try {
        const d = await apiClient.get('/platform/admin/revenue/charts/');
        setData(d);
      } catch {
        setData(null);
      } finally {
        setLoading(false);
      }
    };
    fetchCharts();
  }, []);

  if (loading) return <div className="text-fg-muted">Loading...</div>;
  if (!data) return <div className="text-fg-muted">Failed to load revenue data</div>;

  const bySource = data.by_source.map((r) => ({
    name: r.source,
    revenue: parseFloat(r.revenue),
    full: r.revenue,
  }));
  const byTenant = data.by_tenant_top_10.map((r) => ({
    name: r.tenant_name.length > 15 ? r.tenant_name.slice(0, 12) + '...' : r.tenant_name,
    revenue: parseFloat(r.revenue),
    full: r.tenant_name,
  }));

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-fg">Revenue Analytics</h1>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-surface border border-border rounded-lg p-4">
          <h2 className="text-lg font-semibold text-fg mb-4">Revenue by Source (All Tenants)</h2>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={bySource} layout="vertical" margin={{ left: 20, right: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-chart-grid)" />
                <XAxis type="number" stroke="var(--color-fg-muted)" tickFormatter={(v) => `$${v}`} />
                <YAxis type="category" dataKey="name" stroke="var(--color-fg-muted)" width={80} />
                <Tooltip
                  contentStyle={{ backgroundColor: 'var(--color-chart-tooltip-bg)', border: '1px solid var(--color-chart-tooltip-border)' }}
                  formatter={(value) => [`$${Number(value ?? 0).toFixed(2)}`, 'Revenue']}
                />
                <Bar dataKey="revenue" fill="var(--color-chart-palette-0)" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="bg-surface border border-border rounded-lg p-4">
          <h2 className="text-lg font-semibold text-fg mb-4">Revenue by Tenant (Top 10)</h2>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={byTenant}
                  dataKey="revenue"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                >
                  {byTenant.map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{ backgroundColor: 'var(--color-chart-tooltip-bg)', border: '1px solid var(--color-chart-tooltip-border)' }}
                  formatter={(value, name) => [
                    `$${Number(value ?? 0).toFixed(2)}`,
                    name ?? 'Revenue',
                  ]}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Revenue;
